import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react'
import ComposeEmailModal from '../components/inbox/ComposeEmailModal'
import { listLocalComposeDrafts, listPendingSends, clearPendingSend, PENDING_SEND_RETURNED_EVENT } from '../lib/composeStore'
import { pendingSendNotice } from '../lib/composeDraft'

const ComposeContext = createContext({
  openCompose: () => {},
  closeCompose: () => {},
})

// Gmail-style docked compose: up to MAX_WINDOWS bottom-anchored windows side
// by side, each minimizable to a title bar, so drafting never blocks the rest
// of the app. Drafts auto-save to Gmail (resume from the Drafts tab) and to
// localStorage (abandoned windows reopen minimized on next load).
const MAX_WINDOWS = 3

export function ComposeProvider({ children }) {
  const [windows, setWindows] = useState([])
  const counter = useRef(0)
  const newId = () => `w${Date.now().toString(36)}${counter.current++}`

  // Reopen abandoned composes (navigated away / crashed before closing) as
  // minimized bars. Explicitly closed windows cleared their backup, so only
  // genuinely unfinished drafts come back.
  //
  // Sends that never got a confirmation come back too: one still inside its
  // undo window when the page closed was never handed to Gmail; one that was
  // in flight may or may not have gone out. Both reopen as drafts that keep
  // their send key, so sending again can never produce a second copy. Sends
  // the offline queue owns ('queued') are left to the queue.
  //
  // A pending-send snapshot is the only recoverable record of an uncertain
  // send — it must stay in storage until a window actually represents it.
  // Build every restorable window first, THEN clear only the pending-send
  // records that made it inside MAX_WINDOWS; one excluded by the cap (local
  // drafts filled every slot) is left untouched so it is not lost and can
  // still be recovered on a later load or once a slot frees up.
  useEffect(() => {
    const ids = listLocalComposeDrafts()
    const restored = ids.map(id => ({ id, prefill: null, minimized: true }))
    const sendKeyByWindowId = new Map()
    for (const p of listPendingSends()) {
      if (p.state === 'queued') continue
      const id = newId()
      restored.push({
        id, minimized: true,
        prefill: { restoreSnapshot: p.snapshot, resumeSendKey: p.sendKey, notice: pendingSendNotice(p.state, p.reason) },
      })
      sendKeyByWindowId.set(id, p.sendKey)
    }
    const kept = restored.slice(0, MAX_WINDOWS)
    for (const w of kept) {
      const sendKey = sendKeyByWindowId.get(w.id)
      if (sendKey) clearPendingSend(sendKey)
    }
    if (kept.length) setWindows(kept)
  }, [])

  const openCompose = useCallback((nextPrefill = null) => {
    setWindows(ws => {
      // A restored message (Undo, a refused send) always gets its window, even
      // past the cap — it may be the only copy of what the person wrote.
      if (ws.length >= MAX_WINDOWS && !nextPrefill?.restoreSnapshot) {
        // Cap reached — surface the newest window instead of opening a fourth.
        return ws.map((w, i) => (i === ws.length - 1 ? { ...w, minimized: false } : w))
      }
      return [...ws, { id: newId(), prefill: nextPrefill, minimized: false }]
    })
  }, [])

  const closeCompose = useCallback((id = null) => {
    setWindows(ws => (id ? ws.filter(w => w.id !== id) : ws.slice(0, -1)))
  }, [])

  const toggleMinimize = useCallback((id) => {
    setWindows(ws => ws.map(w => (w.id === id ? { ...w, minimized: !w.minimized } : w)))
  }, [])

  // The offline queue gave up on a send (refused, or retries ran out): open
  // its draft now, with the send key, rather than leaving it behind its
  // 'queued' marker. The window clears the pending copy once it holds it.
  useEffect(() => {
    const onReturned = (ev) => {
      const p = listPendingSends().find(x => x.sendKey === ev?.detail?.sendKey)
      if (!p || p.state === 'queued') return
      openCompose({ restoreSnapshot: p.snapshot, resumeSendKey: p.sendKey, notice: pendingSendNotice(p.state, p.reason), clearPendingKey: p.sendKey })
    }
    window.addEventListener(PENDING_SEND_RETURNED_EVENT, onReturned)
    return () => window.removeEventListener(PENDING_SEND_RETURNED_EVENT, onReturned)
  }, [openCompose])

  // Desktop shell as default email reader: mailto: links clicked anywhere on
  // the Mac arrive as compose prefills. Drain the preload buffer (rather than
  // reading the event payload) so links that LAUNCHED the app — fired before
  // this provider mounted — aren't lost.
  useEffect(() => {
    const bridge = window.chatroneDesktop
    if (!bridge?.takePendingCompose) return
    const drain = () => bridge.takePendingCompose().forEach(p => openCompose(p))
    drain()
    window.addEventListener('chatrone:compose', drain)
    return () => window.removeEventListener('chatrone:compose', drain)
  }, [openCompose])

  return (
    <ComposeContext.Provider value={{ openCompose, closeCompose }}>
      {children}
      {/* right offset leaves the Janet FAB corner clear; pointer-events pass
          through the gaps so the page stays clickable between windows */}
      {windows.length > 0 && (
        <div className="fixed bottom-0 right-0 sm:right-24 z-40 flex items-end gap-3 pl-2 pointer-events-none">
          {windows.map(w => (
            <ComposeEmailModal
              key={w.id}
              isOpen
              docked
              windowId={w.id}
              minimized={w.minimized}
              onToggleMinimize={() => toggleMinimize(w.id)}
              onClose={() => closeCompose(w.id)}
              prefill={w.prefill}
            />
          ))}
        </div>
      )}
    </ComposeContext.Provider>
  )
}

export function useCompose() {
  return useContext(ComposeContext)
}
